import React from "react"
import { Modal, Button, Form } from "react-bootstrap"
import { useVisitsPreferences } from "@/hooks/useLocalStorage"
import { useTranslation } from "react-i18next"

type MapProvider = "google" | "osm" | "mapbox"

const providers: { value: MapProvider; label: string; description: string; icon: string }[] = [
    {
        value: "google",
        label: "Google Maps",
        description: "Alta calidad, requiere clave API y facturación activa.",
        icon: "fa-brands fa-google",
    },
    {
        value: "osm",
        label: "OpenStreetMap",
        description: "Gratuito, sin clave requerida. Ideal como alternativa.",
        icon: "fa-solid fa-map",
    },
    {
        value: "mapbox",
        label: "Mapbox",
        description: "Alta calidad con capa gratuita generosa. Requiere tu propio token.",
        icon: "fa-solid fa-location-dot",
    },
]

interface Props {
    show: boolean
    toggle: (section: string) => void
}

const MapProviderSettings: React.FC<Props> = ({ show, toggle }) => {
    const { t } = useTranslation("global")
    const { mapProvider, setMapProvider, mapboxToken, setMapboxToken } = useVisitsPreferences()

    if (!show) return null

    return (
        <Modal show={show} onHide={() => toggle("")} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="h5 fw-bold">
                    <i className="fa-solid fa-map me-2 text-primary"></i>
                    {t("Proveedor de Mapas")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-muted small mb-3">
                    {t("Selecciona el proveedor de mapas que prefieres usar. Esta preferencia se guarda en este dispositivo.")}
                </p>
                <div className="d-flex flex-column gap-2 mb-3">
                    {providers.map(p => (
                        <div
                            key={p.value}
                            className={`border rounded-3 p-3 cursor-pointer transition-all ${mapProvider === p.value ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "border-secondary border-opacity-25 hover-bg-light"}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => setMapProvider(p.value)}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <Form.Check
                                    type="radio"
                                    name="mapProvider"
                                    id={`map-provider-${p.value}`}
                                    checked={mapProvider === p.value}
                                    onChange={() => setMapProvider(p.value)}
                                    onClick={e => e.stopPropagation()}
                                />
                                <i className={`${p.icon} fa-lg text-primary`} style={{ width: "24px" }}></i>
                                <div>
                                    <div className="fw-semibold text-dark">{p.label}</div>
                                    <div className="text-muted small">{p.description}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {mapProvider === "mapbox" && (
                    <div className="mt-3 p-3 bg-light rounded-3 border">
                        <Form.Label className="fw-semibold text-dark">{t("Token de Mapbox")}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="pk.eyJ1IjoiLi4uIiwiYSI6Ii4uLiJ9..."
                            value={mapboxToken}
                            onChange={e => setMapboxToken(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            {t("Obtén tu token en")}{" "}
                            <a href="https://account.mapbox.com/access-tokens/" target="_blank" rel="noreferrer" className="fw-semibold">
                                account.mapbox.com
                            </a>
                            . {t("El token se guarda solo en este dispositivo.")}
                        </Form.Text>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="py-2">
                <Button variant="primary" size="sm" onClick={() => toggle("")}>
                    {t("Listo / Guardar")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default MapProviderSettings
